import React, { useContext, useEffect, useState } from "react";
import PlanController from "../controllers/PlanController";
import { useAuth } from "./AuthProvider";
import Plan from "../models/Plan";
import { CreatePlanPage } from "../views/FirstPlanPage";
import User from "../models/User";

type PlanWithUsers = Plan & { users: User[] };

type PlanContext = {
    selectedPlan: PlanWithUsers | null;
    plans: PlanWithUsers[];
    createPlan: (name: string, country: string) => Promise<void>;
    selectPlan: (plan: PlanWithUsers) => void;
    reloadPlans: () => void;
    addPlanListener: (origin: string, listener: (plan: PlanWithUsers) => void) => void;
}

const planContext = React.createContext<PlanContext>({} as PlanContext);
let onPlanReload: {[x: string]: ((plan: PlanWithUsers) => void)} = {}

const PlanProvider = ({ children }: { children: React.ReactNode }) => {
    const [plans, setPlans] = useState<PlanWithUsers[]>([]);
    const [selectedPlan, setSelected] = useState<PlanWithUsers | null>(null);
    const [loading, setLoading] = useState(true);

    const auth = useAuth();

    const reloadPlans = () => {
        if (!auth.user) return;
        PlanController.getPlans(auth.user.uid).then(pl => {
            Promise.all(pl.map(p => PlanController.getPlanMembers(p.uid))).then(users => {
                console.log("Users", [...users[0]]);
                const pl2 = pl.map((plan, index) => ({
                    ...plan,
                    users: users[index]
                }));
                

                setPlans(pl2);

                if (pl2.length === 0) {
                    setLoading(false);
                    return;
                }
        
                const nextPlan = !selectedPlan ? pl2[0] : pl2.find(p => p.uid === selectedPlan.uid) || pl2[0];
        
                Object.values(onPlanReload).forEach(listener => listener(nextPlan));
                console.log("First plan: ", nextPlan?.users, [...nextPlan?.users]);
                selectPlan(nextPlan);
                setLoading(false);
            }).catch(error => {
                console.error("Error getting plan members:", error);
                setLoading(false);
            });
        });
    };

    const addPlanListener = (origin: string, listener: (plan: PlanWithUsers) => void) => {
        onPlanReload = {...onPlanReload, [origin]: listener};
    }

    const createPlan = (name: string, country: string): Promise<void> => {
        return new Promise<void>((resolve, reject) => {
            if(!auth.user) {
                reject("User not authenticated");
                return;
            }
            PlanController.createPlan(name, country, auth.user.uid)
            .then(() => {
                reloadPlans();
                resolve();
            }).catch((error) => reject(error));
        });
    }

    const selectPlan = (plan: PlanWithUsers) => {
        console.log("Selected plan:", [...plan.users]);
        setSelected(plan); 
    };
    

    useEffect(() => {
          reloadPlans();
      }, []);

    const value = {
        plans,
        selectedPlan,
        createPlan,
        selectPlan,
        reloadPlans,
        addPlanListener
    };

    return (
        <planContext.Provider value={{...value}}>
            {loading ? <p>Loading...</p> : plans.length === 0 ? <CreatePlanPage /> : children}
        </planContext.Provider>
    );   
}

const PlanConsumer = planContext.Consumer;

const usePlan = () => useContext(planContext);
export { PlanProvider, PlanConsumer, usePlan };